import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  TableHead,
  Paper,
  Button,
  Select,
  MenuItem
} from "@mui/material";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { usecdotStore } from "../../components/cdotStore";

const AssignShifts = ({ refresh }) => {
  const baseURL = process.env.REACT_APP_API_URL;
  const employees = usecdotStore((state) => state.employees);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedShift, setSelectedShift] = useState("");

  const [formData, setFormData] = useState({
    start_date: "",
  });

  const [shifts, setShifts] = useState([]);
  const [assignShifts, setAssignShifts] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch shifts
  const fetchShifts = async () => {
    try {
      const res = await axios.get(`${baseURL}shifts`);
      setShifts(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch assigned shifts
  const fetchAssignShifts = async () => {
    try {
      const res = await axios.get(`${baseURL}shifts/assign`);
      setAssignShifts(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchShifts();
    fetchAssignShifts();
  }, [refresh]);

  // Delete assignment
  const deleteShift = async (id) => {
    if (!window.confirm("Delete this assignment?")) return;

    try {
      await axios.delete(`${baseURL}shifts/assign/${id}`);
      fetchAssignShifts();
    } catch (error) {
      console.error(error);
    }
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        employee_id: selectedEmployee,
        shift_id: selectedShift,
        date: formData.start_date,
      };

      await axios.post(`${baseURL}shifts/assign`, submitData);

      fetchAssignShifts();
      resetForm();
      setIsAddModalOpen(false);

    } catch (error) {
      console.error(error);
    }
  };

  // Input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({ start_date: "" });
    setSelectedEmployee("");
    setSelectedShift("");
  };

  // Pagination
  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const visibleRows =
    rowsPerPage > 0
      ? assignShifts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : assignShifts;

  // Helpers to show names
  const getEmployeeName = (id) =>
    employees.find((e) => e.id === id)?.emp_name || id;

  const getShiftName = (id) =>
    shifts.find((s) => s.id === id)?.name || id;

  return (
    <div>
      <Box>
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Assign Shift</h2>
          <Button onClick={() => setIsAddModalOpen(true)}>
            + Assign Shift
          </Button>
        </div>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{getEmployeeName(s.employee_id)}</TableCell>
                  <TableCell>{getShiftName(s.shift_id)}</TableCell>
                  <TableCell>{s.date}</TableCell>
                  <TableCell>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={() => deleteShift(s.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  count={assignShifts.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle>Assign New Shift</CardTitle>
              <CardDescription>Assign shift to employee</CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">

                <div className="grid grid-cols-2 gap-4">

                  {/* Employee */}
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">Select Employee</MenuItem>
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.emp_name}
                      </MenuItem>
                    ))}
                  </Select>

                  {/* Shift */}
                  <Select
                    value={selectedShift}
                    onChange={(e) => setSelectedShift(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">Select Shift</MenuItem>
                    {shifts.map((shift) => (
                      <MenuItem key={shift.id} value={shift.id}>
                        {shift.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                {/* Date */}
                <Input
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    Assign Shift
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      setIsAddModalOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>

              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AssignShifts;